import React, { useCallback, useMemo, useState, useEffect } from 'react';

import { Flex, useTheme } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import Select, {
  ControlProps,
  components,
  StylesConfig,
  DropdownIndicatorProps,
} from 'react-select';

import { enabledLanguages, getLanguageNameByCode } from '@/localization';

import ShimmerImage from '@/elements/ShimmerImage';

import { StorageKeysEnum } from '@/enum/storageKeys.enum';

import { useLocalStorage } from '@/hooks/useLocalStorage';

import { countryFlagByCode } from '@/utils/countryFlagByName';

type LanguageItemType = {
  value: string;
  label: string;
  image: string;
};

const LangSwitcherOption = ({
  innerProps,
  label,
  data,
}: {
  innerProps: any;
  label: string;
  data: LanguageItemType;
}) => (
  <Flex align='center' p='5px' {...innerProps}>
    <ShimmerImage
      alt={label}
      borderRadius='50%'
      h='auto'
      mr='5px'
      skeletonProps={{ width: '20px', borderRadius: '50%', mr: '5px' }}
      src={data.image}
      w='20px'
    />
    {label}
  </Flex>
);

const LangSwitcherControl = ({
  children,
  ...props
}: ControlProps<any, false>) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { lang } = props.selectProps;

  return (
    <components.Control {...props}>
      <ShimmerImage
        borderRadius='50%'
        skeletonProps={{ borderRadius: '50%' }}
        src={lang}
      />
      {children}
    </components.Control>
  );
};

const DropdownIndicator = (props: DropdownIndicatorProps<any, false>) => {
  const { selectProps } = props;
  let arrow;
  if (selectProps.menuIsOpen) {
    arrow = <BiChevronUp size='20px' />;
  } else {
    arrow = <BiChevronDown size='20px' />;
  }
  return (
    <components.DropdownIndicator {...props}>
      {arrow}
    </components.DropdownIndicator>
  );
};

const LangSwitcher: React.FC = () => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { value: storageLang, setItem: setLang } = useLocalStorage<string>(
    StorageKeysEnum.i18nextLng,
  );

  const languageOptions = useMemo<LanguageItemType[]>(
    () =>
      enabledLanguages.map((lang) => ({
        value: lang.toLowerCase(),
        label: getLanguageNameByCode(lang),
        image: countryFlagByCode(lang),
      })),
    [],
  );

  const defaultLanguage = useMemo(
    () =>
      languageOptions.find((x) => x.value === storageLang) ||
      languageOptions[0],
    [languageOptions, storageLang],
  );

  const [currentLang, setCurrentLang] = useState(defaultLanguage);

  useEffect(() => setCurrentLang(defaultLanguage), [defaultLanguage]);

  const onLangChange = useCallback(
    (opt: LanguageItemType) => {
      setCurrentLang(opt);
      i18n.changeLanguage(opt.value, () => setLang(opt.value));
    },
    [i18n, setLang],
  );

  const customStyles = useMemo<StylesConfig<any, false, any>>(
    () => ({
      control: (provided) => ({
        ...provided,
        background: theme.colors.langSwitcher.bg,
        borderRadius: '20px',
        border: '0',
        borderColor: theme.colors.langSwitcher.border,
        height: '30px',
        minHeight: '30px',
        padding: '3px',
        outline: '0',
        boxShadow: 'none',
        cursor: 'pointer',

        img: {
          width: '22px',
          height: '22px',
        },
      }),
      valueContainer: (props) => ({
        ...props,
        padding: '0 6px',

        div: {
          margin: '0',
        },
      }),
      indicatorsContainer: (props) => ({
        ...props,
        div: {
          padding: '0 3px 0 0',
          color: theme.colors.langSwitcher.icon,
        },
      }),
      singleValue: (props) => ({
        ...props,
        color: theme.colors.langSwitcher.text,
      }),
      option: (props) => ({
        ...props,
        cursor: 'pointer',
        color: theme.colors.langSwitcher.text,
      }),
      menuPortal: (provided) => ({
        ...provided,
        border: 'none',
        zIndex: 9999,
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 1,
        top: '0',
        marginTop: '0px',
        border: 'none',
        boxShadow: 'none',
        color: theme.colors.langSwitcher.text,
        background: theme.colors.langSwitcher.bg,
        cursor: 'pointer',
      }),
      indicatorSeparator: () => ({ display: 'none' }),
    }),
    [
      theme.colors.langSwitcher.bg,
      theme.colors.langSwitcher.border,
      theme.colors.langSwitcher.icon,
      theme.colors.langSwitcher.text,
    ],
  );

  return (
    <Select
      components={{
        Option: LangSwitcherOption,
        Control: LangSwitcherControl,
        DropdownIndicator,
      }}
      defaultValue={currentLang}
      isSearchable={false}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      lang={currentLang.image}
      onChange={onLangChange}
      options={languageOptions}
      styles={customStyles}
      value={languageOptions.find((x) => x.value === currentLang.value)}
    />
  );
};

export default LangSwitcher;
